"use client";

import { useFieldArray, useFormContext } from "react-hook-form";
import {
  Card,
  Checkbox,
  Radio,
  Typography,
} from "@cappelendamm/design-system/react";
import Image from "next/image";
import { getImageBySize } from "@cappelendamm/catalogue/crystallize/utils/images.js";
import {
  GiftOption,
  SubscriptionGiftsForm,
} from "@/checkout/Sections/SubscriptionSection/types";
import { getCountNoun } from "@/utils/formatCountLabel";

type ValidGiftFieldPath = "gifts" | `giftSections.${number}.options`;

export type SubscriptionGiftsProps = {
  count: number;
  items: GiftOption[];
  fieldArrayName: ValidGiftFieldPath;
  sectionIndex?: number;
  totalSections?: number;
  sectionError?: string;
};

export const SubscriptionGifts = ({
  count,
  items,
  fieldArrayName,
  sectionIndex,
  totalSections,
  sectionError,
}: SubscriptionGiftsProps) => {
  const {
    register,
    watch,
    setValue,
    trigger,
    formState: { errors, isSubmitted },
    control,
  } = useFormContext<SubscriptionGiftsForm>();

  useFieldArray({
    control,
    name: fieldArrayName,
    rules: {
      validate: (value) => {
        const giftOptions: GiftOption[] = Array.isArray(value)
          ? (value as GiftOption[])
          : [];
        const selectedCount: number = giftOptions.filter(
          (item: GiftOption) => item.selected,
        ).length;
        if (selectedCount !== count)
          return `Du må velge ${count} ${getCountNoun(count, "gave", "gaver")}`;
        return true;
      },
    },
  });

  const selectedGifts: GiftOption[] =
    (watch(fieldArrayName) as GiftOption[] | undefined) ?? [];
  const selectedCount: number = selectedGifts.filter(
    (item: GiftOption) => item.selected,
  ).length;
  const allPreSelected: boolean = count === items.length;
  const canUseRadioControl = !allPreSelected && count === 1;
  const canUseCheckboxControl = !allPreSelected && count > 1;
  const isFullySelected: boolean = selectedCount === count;

  if (count === 0 || items.length === 0) return null;

  const titleBase: string =
    allPreSelected || isFullySelected
      ? count > 1
        ? "Dine velkomstgaver"
        : "Din velkomstgave"
      : "Velg velkomstgave";
  const title: string =
    sectionIndex !== undefined && (totalSections ?? 0) > 1
      ? `${titleBase} (${sectionIndex + 1})`
      : titleBase;
  const subtitle: string = `Velg ${count} av ${items.length} alternativ${items.length > 1 ? "er" : ""}`;
  const showSubtitle: boolean = !allPreSelected;
  const rootGiftErrorMessage: string | undefined = errors.gifts?.root
    ?.message as string | undefined;

  const handleRadioChange = (selectedIndex: number): void => {
    items.forEach((_, i): void => {
      setValue(`${fieldArrayName}.${i}.selected`, i === selectedIndex, {
        shouldDirty: true,
      });
    });
    if (isSubmitted) void trigger(fieldArrayName);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-1">
        <h3 className="text-center text-2xl font-semibold">{title}</h3>
        {showSubtitle && (
          <p className="text-center text-base text-gray-600">{subtitle}</p>
        )}
        {!sectionError && rootGiftErrorMessage && (
          <p className="text-left text-status-error-500">
            {rootGiftErrorMessage}
          </p>
        )}
        {sectionError && (
          <p className="text-left text-status-error-500">{sectionError}</p>
        )}
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {items.map((item: GiftOption, index: number) => {
          const { sku, name, description, images, selected: isSelected } = item;
          const image = getImageBySize(images, 200);
          const inputId: string = sku ?? `${fieldArrayName}.${index}.selected`;
          const itemId: string = sku ?? `${fieldArrayName}-${index}`;

          return (
            <Card
              key={itemId}
              className={`flex cursor-pointer flex-col overflow-hidden border-2 p-0 transition-colors ${
                isSelected
                  ? "border-black"
                  : "border-stroke-default hover:border-black"
              }`}
              htmlFor={inputId}
              as="label"
            >
              <div>
                <div className="flex h-52 w-full items-center justify-center bg-white p-6">
                  {image.src ? (
                    <Image
                      className="h-44 w-auto object-contain"
                      height={176}
                      src={image.src}
                      alt={image.altText}
                      width={Math.round(176 * (image.aspectRatio ?? 3 / 4))}
                    />
                  ) : (
                    <div className="aspect-[3/4] h-44" />
                  )}
                </div>
                <div className="flex flex-1 items-end bg-white p-6">
                  <div className="flex w-full items-end justify-between gap-3">
                    <div className="flex flex-col gap-1">
                      <Typography variant="title-md" as="p">
                        {name}
                      </Typography>
                      {description && (
                        <p className="text-base">{description}</p>
                      )}
                    </div>
                    {canUseRadioControl && (
                      <Radio
                        name={`gift-selection-${sectionIndex ?? 0}`}
                        value={index}
                        checked={isSelected}
                        onChange={() => handleRadioChange(index)}
                        id={inputId}
                        aria-label={`Velg ${name}`}
                      />
                    )}
                    {canUseCheckboxControl && (
                      <Checkbox
                        checked={isSelected}
                        {...register(`${fieldArrayName}.${index}.selected`, {
                          onChange: () => {
                            if (isSubmitted) void trigger(fieldArrayName);
                          },
                        })}
                        id={inputId}
                        disabled={!isSelected && selectedCount >= count}
                        aria-label={`Velg ${name}`}
                      />
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
